import { useCallback } from 'react';
import { useLocalStorageState } from '@srm/ui/hooks/useLocalStorageState';
import { useTypedSelector } from '@hooks/useTypedSelector';

/**
 * Масштаб шрифта таблиц «Выбора победителя». Множитель распространяется через
 * CSS-переменную --ws-font-scale на корне компонента: каждый font-size задан
 * как calc(Npx * var(--ws-font-scale, 1)), поэтому меняется только типографика —
 * геометрия колонок, скролла и сплиттера остаётся прежней (px-расчёты
 * HorizontalPager / Allotment не рассинхронизируются).
 *
 * Контролом управляет выпадайка «Пользовательские настройки» в навбаре, а сами
 * таблицы лишь читают значение и выставляют --ws-font-scale на своём корне. Так
 * как навбар и таблица — разные поддеревья DOM, прямой проброс пропсом невозможен;
 * значение хранится через useLocalStorageState (@srm/ui), который синхронизирует
 * все инстансы хука в пределах вкладки (custom-event) и между вкладками
 * (storage-event) — правка из навбара мгновенно долетает до уже открытой таблицы.
 * Ключ — per-user, как у соседних настроек (scrollByContentInTables).
 */
export const FONT_SCALE_MIN = 0.9;
export const FONT_SCALE_MAX = 1.6;
export const FONT_SCALE_STEP = 0.1;
export const FONT_SCALE_DEFAULT = 1;

/** Приводит к шагу 0.1 и зажимает в [MIN, MAX] (защита от floating-point дребезга). */
const normalize = (v: number): number => Math.min(FONT_SCALE_MAX, Math.max(FONT_SCALE_MIN, Math.round(v * 10) / 10));

export interface IFontScale {
    /** Текущий множитель (1 = 100%). */
    scale: number;
    /** Множитель в процентах для подписи (например 110). */
    percent: number;
    increase: () => void;
    decrease: () => void;
    /** Сброс к 100%. */
    reset: () => void;
    canIncrease: boolean;
    canDecrease: boolean;
}

export function useFontScale(): IFontScale {
    const username = useTypedSelector((state) => state.auth.user?.username);
    const [raw, setRaw] = useLocalStorageState<number>(
        `user-${username ?? 'default'}.winnerSelection.fontScale`,
        FONT_SCALE_DEFAULT,
    );

    const scale = normalize(typeof raw === 'number' && Number.isFinite(raw) ? raw : FONT_SCALE_DEFAULT);

    const apply = useCallback(
        (delta: number) =>
            setRaw((prev) => {
                const base = typeof prev === 'number' && Number.isFinite(prev) ? prev : FONT_SCALE_DEFAULT;
                return normalize(base + delta);
            }),
        [setRaw],
    );

    const increase = useCallback(() => apply(FONT_SCALE_STEP), [apply]);
    const decrease = useCallback(() => apply(-FONT_SCALE_STEP), [apply]);
    const reset = useCallback(() => setRaw(FONT_SCALE_DEFAULT), [setRaw]);

    return {
        scale,
        percent: Math.round(scale * 100),
        increase,
        decrease,
        reset,
        canIncrease: scale < FONT_SCALE_MAX,
        canDecrease: scale > FONT_SCALE_MIN,
    };
}
